import React, { useEffect, useState } from "react";
import { CustomModal, FiltersButton, InfoBanner, TableHeader } from "@services/ui-components";
import { Trans, useTranslation } from "react-i18next";
import { useReactiveVar } from "@apollo/client";
import { TechMapsFilterI } from "../types";
import { Button, Col, Container, Row } from "react-bootstrap";
import TechnologicalMapsFilterWrapper from "./TechnologicalMapsFilterWrapper";
import TechnologicalMapsTable from "./TechnologicalMapsTable";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import CreateTechnologicalMap from "../CreateTechnologicalMap/CreateTechnologicalMap";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ToastComponent from "../../ui-components/ToastComponent/ToastComponent";
import { useToast } from "../../../hooks/useToast";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { CompanyAccess } from "@me-team/host/main/graphql/types";
import {
    getCurrentStepFromLocalStorage,
    getWarehouseTutorialType,
    isWarehouseTutorialActive,
    moveStepInLocalStorage,
    StepDirection,
} from "../../Joyride/WarehouseTutorial/joyrideStepHelpers";
import WarehouseJoyrideWrapper from "../../Joyride/WarehouseTutorial/WarehouseJoyrideWrapper";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

const TechnologicalMapsList: React.FC = () => {
    const { t } = useTranslation();
    const { branchId } = useParams();
    const navigate = useNavigate();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const canCreateTechMap = useRoleAccess(CompanyAccess.TechMapCreate);
    const canCreateTechMapCategory = useRoleAccess(CompanyAccess.TechMapCategoryCreate);
    const canOpenTechMapProducts = useRoleAccess(CompanyAccess.TechMapProductListView);
    const [searchParams] = useSearchParams();
    const categoryId: number = Number(searchParams.get("id")) || null;

    const [showFilters, setShowFilters] = useState(false);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const [showCreateTechMapModal, setShowCreateTechMapModal] = useState(false);
    const [refetchTechMaps, setRefetchTechMaps] = useState(null);
    const [isTechMapModalCreated, setIsTechMapModalCreated] = useState<boolean>(false);
    const [getTechMapId, setGetTechMapId] = useState<number | string | null>(0);
    const { showToast, toastText, toggleShowToast, setShowToast } = useToast();
    const tutorialActive = isWarehouseTutorialActive();
    const tutorialType = getWarehouseTutorialType();
    const startStep = getCurrentStepFromLocalStorage();

    const handleSetRefetch = (refetchTechMaps: any) => {
        setRefetchTechMaps(() => refetchTechMaps);
    };

    const initialValueFilter: TechMapsFilterI = {
        search: null,
        categoryId: null,
    };

    const [filters, setFilters] = useState<TechMapsFilterI>(initialValueFilter);

    useEffect(() => {
        categoryId && toggleFilters();
    }, [categoryId]);

    const toggleFilters = () => setShowFilters(!showFilters);
    const handleCreateTechMap = () => {
        if (!canCreateTechMap) return;

        setShowCreateTechMapModal((prevState) => !prevState);
    };
    const handleCloseCreateTechMapModal = () => {
        if (tutorialActive) {
            moveStepInLocalStorage(StepDirection.PREV);
        }
        setShowCreateTechMapModal(false);
    };
    const handleCloseCreateTechMapModalAfterSubmit = () => setShowCreateTechMapModal(false);
    const handleTechMapModalCreatedClose = () => setIsTechMapModalCreated(false);
    const handleSetTechMapModalCreated = () => setIsTechMapModalCreated(true);
    const handleTechMapModalCreatedToProducts = () => {
        if (!canOpenTechMapProducts) return;

        navigate(`/warehouse/technological-maps/${currentCompanyId}/${getTechMapId}/products/branch/${branchId}`);
    };

    return (
        <div className="mb-9 px-2 px-lg-0">
            <InfoBanner>
                <Trans
                    i18nKey="infoBannerForTechnologicalMapsList"
                    components={{
                        basket: <i className="bi bi-basket fs-7" />,
                        bookmark: <i className="bi bi-bookmark fs-7" />,
                    }}
                />
            </InfoBanner>
            <TableHeader
                title={t("List of Technological Maps")}
                paths={[
                    {
                        path: `/warehouse/technological-maps/${currentCompanyId}/branch/${branchId}`,
                        label: t("Warehouse accounting"),
                    },
                    {
                        path: `/warehouse/technological-maps/${currentCompanyId}/branch/${branchId}`,
                        label: t("List of Technological Maps"),
                    },
                ]}
                colSize={6}
                btnColSize={6}
                buttons={
                    <Row className="gx-3 justify-content-end align-items-center mt-4 mt-lg-0">
                        {canCreateTechMap ? (
                            <Col xs={12} sm={6} lg={8} xl={6} xxl={5}>
                                <Button
                                    variant="primary"
                                    className="w-100 text-white mb-3 mb-lg-0 text-truncate"
                                    onClick={() => {
                                        tutorialActive && moveStepInLocalStorage(StepDirection.NEXT);
                                        handleCreateTechMap();
                                    }}
                                    data-tour="create-tech-map-btn"
                                >
                                    <i className="bi bi-plus-lg fw-bold me-1 w-100 h-100"></i>
                                    {t("Create New Technological Map")}
                                </Button>
                            </Col>
                        ) : null}
                        <Col xs={12} sm={6} lg={4} xl={3}>
                            <FiltersButton
                                filter={filters}
                                showFilters={showFilters}
                                onClick={toggleFilters}
                                classes="mb-3 mb-lg-0"
                            />
                        </Col>
                    </Row>
                }
                showFilters={showFilters}
            >
                <TechnologicalMapsFilterWrapper setFilters={setFilters} />
            </TableHeader>
            <TechnologicalMapsTable
                filters={filters}
                showCreateTechMapModal={showCreateTechMapModal}
                handleCreateTechMap={handleCreateTechMap}
                setRefetchTechMaps={handleSetRefetch}
                toggleShowToast={toggleShowToast}
            />

            {canCreateTechMap && showCreateTechMapModal && (
                <CreateTechnologicalMap
                    show={showCreateTechMapModal}
                    onClose={handleCloseCreateTechMapModal}
                    onSubmitClose={handleCloseCreateTechMapModalAfterSubmit}
                    refetchTechMaps={refetchTechMaps}
                    toggleShowToast={toggleShowToast}
                    handleSetTechMapModalCreated={canOpenTechMapProducts ? handleSetTechMapModalCreated : undefined}
                    setGetTechMapId={setGetTechMapId}
                    dataTour={tutorialActive && "create-tech-map-form"}
                    canCreateTechMap={canCreateTechMap}
                    canCreateTechMapCategory={canCreateTechMapCategory}
                />
            )}

            <CustomModal
                show={isTechMapModalCreated}
                handleClose={handleTechMapModalCreatedClose}
                title={
                    <Container>
                        <Row>
                            <h5 className="m-0 p-0 fw-normal">{t("Tech card created!")}</h5>
                        </Row>
                    </Container>
                }
                body={
                    <Container>
                        <Row>
                            <Col>
                                <span className="fs-6 me-1">
                                    {t("Do you want to configure the products for this technological map?")}
                                </span>
                            </Col>
                        </Row>
                    </Container>
                }
                buttons={[
                    {
                        text: t("Later"),
                        onClick: handleTechMapModalCreatedClose,
                        variant: "outline-primary",
                        className: "w-100 fw-normal",
                    },
                    ...(canOpenTechMapProducts
                        ? [
                              {
                                  text: t("Go to settings"),
                                  onClick: handleTechMapModalCreatedToProducts,
                                  variant: "primary",
                                  className: "w-100 fw-normal",
                              },
                          ]
                        : []),
                ]}
            />

            {tutorialActive && tutorialType && (
                <WarehouseJoyrideWrapper tutorialType={tutorialType} startStep={startStep} />
            )}

            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
            {showToast && <ToastComponent show={showToast} setShow={setShowToast} text={toastText} />}
        </div>
    );
};

export default TechnologicalMapsList;
